import { z } from "zod";

import { LOCALES } from "@/i18n/types";

export const navigationItemSchema = z.object({
  href: z.string(),
  label: z.string(),
});

export const footerGroupSchema = z.object({
  links: z.array(navigationItemSchema),
  title: z.string(),
});

export const socialLinkSchema = z.object({
  href: z.string().url(),
  label: z.string(),
});

export const themeTokensSchema = z.object({
  accent: z.string(),
  background: z.string(),
  buttonRadius: z.string(),
  fontBody: z.string(),
  fontDisplay: z.string(),
  foreground: z.string(),
  heroGlow: z.string(),
  primary: z.string(),
  radius: z.string(),
  sectionSpacing: z.string(),
  secondary: z.string(),
  shadowStrength: z.string(),
  surface: z.string(),
  surfaceMuted: z.string(),
  surfaceStrong: z.string(),
});

export const businessDetailsSchema = z.object({
  addressLines: z.array(z.string()).min(1),
  email: z.email(),
  hours: z.string(),
  phone: z.string(),
});

export const heroSectionSchema = z.object({
  ctaHref: z.string(),
  ctaLabel: z.string(),
  description: z.string(),
  eyebrow: z.string(),
  image: z.string().optional(),
  secondaryHref: z.string(),
  secondaryLabel: z.string(),
  title: z.string(),
});

export const featureItemSchema = z.object({
  description: z.string(),
  image: z.string().optional(),
  title: z.string(),
});

export const featureSectionSchema = z.object({
  description: z.string(),
  eyebrow: z.string(),
  items: z.array(featureItemSchema).min(1),
  title: z.string(),
});

export const aboutSectionSchema = z.object({
  body: z.array(z.string()).min(1),
  eyebrow: z.string(),
  highlights: z.array(z.string()).min(1),
  title: z.string(),
});

export const ctaSectionSchema = z.object({
  description: z.string(),
  eyebrow: z.string().optional(),
  href: z.string(),
  label: z.string(),
  title: z.string(),
});

export const faqItemSchema = z.object({
  answer: z.string(),
  question: z.string(),
});

export const faqSectionSchema = z.object({
  description: z.string(),
  eyebrow: z.string(),
  items: z.array(faqItemSchema).min(1),
  title: z.string(),
});

export const serviceItemSchema = z.object({
  audience: z.string(),
  ctaHref: z.string().optional(),
  ctaLabel: z.string().optional(),
  description: z.string(),
  highlights: z.array(z.string()).min(1),
  summary: z.string(),
  title: z.string(),
});

export const serviceDecisionItemSchema = z.object({
  description: z.string(),
  service: z.string(),
  title: z.string(),
});

export const servicesListSectionSchema = z.object({
  decisionDescription: z.string(),
  decisionItems: z.array(serviceDecisionItemSchema).min(1),
  decisionTitle: z.string(),
  description: z.string(),
  eyebrow: z.string(),
  items: z.array(serviceItemSchema).min(1),
  navigatorTitle: z.string(),
  serviceCountLabel: z.string().optional(),
  title: z.string(),
});

export const processStepSchema = z.object({
  description: z.string(),
  title: z.string(),
});

export const processSectionSchema = z.object({
  description: z.string(),
  eyebrow: z.string(),
  image: z.string().optional(),
  steps: z.array(processStepSchema).min(1),
  title: z.string(),
});

export const galleryItemSchema = z.object({
  alt: z.string(),
  caption: z.string(),
  src: z.string(),
});

export const gallerySectionSchema = z.object({
  description: z.string().optional(),
  eyebrow: z.string(),
  items: z.array(galleryItemSchema).min(1),
  title: z.string(),
});

export const testimonialItemSchema = z.object({
  author: z.string(),
  quote: z.string(),
  role: z.string(),
});

export const testimonialsSectionSchema = z.object({
  description: z.string().optional(),
  eyebrow: z.string(),
  items: z.array(testimonialItemSchema).min(1),
  title: z.string(),
});

export const pageIntroSchema = z.object({
  description: z.string(),
  eyebrow: z.string(),
  title: z.string(),
});

export const legalSectionSchema = z.object({
  description: z.string(),
  sections: z.array(
    z.object({
      body: z.array(z.string()).min(1),
      title: z.string(),
    }),
  ),
  title: z.string(),
  updatedAt: z.string(),
});

export const inquiryActionSchema = z.object({
  href: z.string(),
  label: z.string(),
});

export const inquiryFieldOptionSchema = z.object({
  label: z.string(),
  value: z.string(),
});

export const inquiryFieldSchema = z.object({
  autocomplete: z.string().optional(),
  label: z.string(),
  name: z.string(),
  options: z.array(inquiryFieldOptionSchema).optional(),
  placeholder: z.string().optional(),
  required: z.boolean().default(true),
  type: z.enum([
    "date",
    "email",
    "number",
    "select",
    "tel",
    "text",
    "textarea",
  ]),
});

export const inquirySectionSchema = z.object({
  actions: z.array(inquiryActionSchema).min(1),
  asideTitle: z.string(),
  availability: z.array(z.string()).min(1),
  description: z.string(),
  eyebrow: z.string(),
  fields: z.array(inquiryFieldSchema).min(1),
  formEyebrow: z.string(),
  formIntro: z.string(),
  privacyNote: z.string(),
  submitError: z.string(),
  submitPending: z.string(),
  submitLabel: z.string(),
  submitSuccess: z.string(),
  successHint: z.string(),
  title: z.string(),
});

export const localeContentSchema = z.object({
  footerGroups: z.array(footerGroupSchema).min(1),
  home: z.object({
    brandIntro: aboutSectionSchema,
    cta: ctaSectionSchema,
    eventTypes: featureSectionSchema,
    hero: heroSectionSchema,
    processPreview: processSectionSchema,
    servicesPreview: featureSectionSchema,
  }),
  inquiry: inquirySectionSchema,
  legal: legalSectionSchema,
  navigation: z.array(navigationItemSchema).min(3),
  pages: z.object({
    about: pageIntroSchema,
    home: pageIntroSchema,
    inquiry: pageIntroSchema,
    legal: pageIntroSchema,
    lookbook: pageIntroSchema,
    process: pageIntroSchema,
    services: pageIntroSchema,
  }),
  sections: z.object({
    aboutDifferentiators: featureSectionSchema,
    aboutFounder: aboutSectionSchema,
    aboutPhilosophy: pageIntroSchema,
    lookbookGallery: gallerySectionSchema,
    processJourney: processSectionSchema,
    servicesList: servicesListSectionSchema,
    servicesSupportCta: ctaSectionSchema,
  }),
  site: z.object({
    description: z.string(),
    name: z.string(),
    tagline: z.string(),
  }),
});

export const sharedSiteSchema = z.object({
  business: businessDetailsSchema,
  defaultOgImage: z.string().default("/og/default.png"),
  locales: z.array(z.enum(LOCALES as unknown as [string, ...string[]])).min(1),
  organization: z.object({
    legalName: z.string(),
    logo: z.string().optional(),
    url: z.string(),
  }),
  socialLinks: z.array(socialLinkSchema).default([]),
  theme: themeTokensSchema,
  twitterHandle: z.string().optional(),
});

export const siteConfigSchema = z.object({
  shared: sharedSiteSchema,
  locales: z.record(
    z.enum(LOCALES as unknown as [string, ...string[]]),
    localeContentSchema,
  ),
});

export type NavigationItem = z.infer<typeof navigationItemSchema>;
export type FooterGroup = z.infer<typeof footerGroupSchema>;
export type SocialLink = z.infer<typeof socialLinkSchema>;
export type ThemeTokens = z.infer<typeof themeTokensSchema>;
export type BusinessDetails = z.infer<typeof businessDetailsSchema>;
export type HeroSection = z.infer<typeof heroSectionSchema>;
export type FeatureItem = z.infer<typeof featureItemSchema>;
export type FeatureSection = z.infer<typeof featureSectionSchema>;
export type AboutSection = z.infer<typeof aboutSectionSchema>;
export type CtaSection = z.infer<typeof ctaSectionSchema>;
export type FaqItem = z.infer<typeof faqItemSchema>;
export type FaqSection = z.infer<typeof faqSectionSchema>;
export type ServiceItem = z.infer<typeof serviceItemSchema>;
export type ServiceDecisionItem = z.infer<typeof serviceDecisionItemSchema>;
export type ServicesListSection = z.infer<typeof servicesListSectionSchema>;
export type ProcessStep = z.infer<typeof processStepSchema>;
export type ProcessSection = z.infer<typeof processSectionSchema>;
export type GalleryItem = z.infer<typeof galleryItemSchema>;
export type GallerySection = z.infer<typeof gallerySectionSchema>;
export type TestimonialItem = z.infer<typeof testimonialItemSchema>;
export type TestimonialsSection = z.infer<typeof testimonialsSectionSchema>;
export type PageIntro = z.infer<typeof pageIntroSchema>;
export type LegalSection = z.infer<typeof legalSectionSchema>;
export type InquiryAction = z.infer<typeof inquiryActionSchema>;
export type InquiryFieldOption = z.infer<typeof inquiryFieldOptionSchema>;
export type InquiryField = z.infer<typeof inquiryFieldSchema>;
export type InquirySection = z.infer<typeof inquirySectionSchema>;
export type LocaleContent = z.infer<typeof localeContentSchema>;
export type SharedSiteConfig = z.infer<typeof sharedSiteSchema>;
export type SiteConfig = z.infer<typeof siteConfigSchema>;
